import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail, isNetworkError } from "../../lib/api";
import { deptLabel } from "../../lib/labels";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";
import PageHeader from "../../components/system/PageHeader";
import Card from "../../components/system/Card";
import Button from "../../components/system/Button";
import Input from "../../components/system/Input";
import OfflineUnavailable from "../../components/system/OfflineUnavailable";

function AddDeptModal({ open, onCancel, onCreated }) {
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => { if (!open) setName(""); }, [open]);
  if (!open) return null;

  const submit = async () => {
    const trimmed = name.trim();
    if (!trimmed) { toast.error("Name is required"); return; }
    setSubmitting(true);
    try {
      await api.post("/departments", { name: trimmed });
      toast.success("Department added");
      onCreated();
    } catch (e) { toast.error(formatApiErrorDetail(e)); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/60 p-4" data-testid="dept-add-modal">
      <Card variant="raised" className="w-full sm:max-w-md p-6">
        <div className="flex items-start justify-between mb-3">
          <h3 className="font-display font-black text-xl tracking-tight">New department</h3>
          <button onClick={onCancel} className="p-2 -mt-2 -mr-2 rounded-button text-ink-2 hover:text-ink" aria-label="Close"><X size={18} /></button>
        </div>
        <Input
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
          placeholder="e.g. PRODUCE"
          className="mb-4"
          data-testid="new-department-input"
        />
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
          <Button tier="secondary" onClick={onCancel} data-testid="dept-add-cancel">Cancel</Button>
          <Button tier="primary" onClick={submit} disabled={submitting} data-testid="dept-add-confirm">
            {submitting ? "Adding…" : "Add department"}
          </Button>
        </div>
      </Card>
    </div>
  );
}

function ConfirmDeleteModal({ open, dept, onCancel, onConfirm, submitting }) {
  if (!open || !dept) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/60 p-4" data-testid="dept-delete-modal">
      <Card variant="raised" className="w-full sm:max-w-md p-6">
        <div className="flex items-start justify-between mb-3">
          <h3 className="font-display font-black text-xl tracking-tight">Delete department?</h3>
          <button onClick={onCancel} className="p-2 -mt-2 -mr-2 rounded-button text-ink-2 hover:text-ink" aria-label="Close"><X size={18} /></button>
        </div>
        <p className="text-sm text-ink/70 mb-2">
          You are about to delete <span className="font-display font-bold text-ink">{deptLabel(dept.name)}</span>.
        </p>
        <p className="text-xs text-ink/60 mb-5">
          The backend will block this if any users, products, or inventory are still attached to the department.
          Otherwise it will be removed.
        </p>
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
          <Button tier="secondary" onClick={onCancel} data-testid="dept-delete-cancel">Cancel</Button>
          <Button
            tier="primary"
            onClick={onConfirm}
            disabled={submitting}
            className="bg-status-expired hover:bg-status-expired"
            data-testid="dept-delete-confirm"
          >
            {submitting ? "Deleting…" : "Delete"}
          </Button>
        </div>
      </Card>
    </div>
  );
}

export default function DepartmentsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [unavailableOffline, setUnavailableOffline] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/departments");
      setItems([...data].sort((a, b) => deptLabel(a.name).localeCompare(deptLabel(b.name))));
      setUnavailableOffline(false);
    } catch (e) {
      if (isNetworkError(e)) {
        setItems([]);
        setUnavailableOffline(true);
      } else {
        toast.error(formatApiErrorDetail(e));
      }
    }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const onCreated = async () => { setShowAdd(false); await load(); };

  const doDelete = async () => {
    if (!pendingDelete) return;
    setSubmitting(true);
    try {
      await api.delete(`/departments/${pendingDelete.id}`);
      toast.success(`Deleted ${deptLabel(pendingDelete.name)}`);
      setPendingDelete(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e)); }
    finally { setSubmitting(false); }
  };

  return (
    <div data-testid="admin-departments-page">
      <PageHeader
        eyebrow="Store structure"
        title="Departments"
        subtitle={
          <span data-testid="dept-count">
            {loading ? (
              <span className="text-ink-3">Loading departments…</span>
            ) : unavailableOffline ? (
              <span className="text-ink-3">Reconnect to load departments</span>
            ) : (
              <>
                <span className="font-mono text-ink">{items.length}</span> active department{items.length === 1 ? "" : "s"}
              </>
            )}
          </span>
        }
        actions={
          <Button tier="primary" onClick={() => setShowAdd(true)} data-testid="add-department-button">
            <Plus size={16} /> Add department
          </Button>
        }
      />

      {unavailableOffline ? (
        <OfflineUnavailable
          title="Departments unavailable offline"
          description="Reconnect to load department data."
          onRetry={load}
          data-testid="departments-offline-unavailable"
        />
      ) : (
      <div className="flex flex-wrap gap-2" data-testid="dept-chip-grid">
        {loading ? (
          <div className="flex flex-wrap gap-2 w-full" aria-busy="true" data-testid="dept-loading-skeleton">
            {Array.from({ length: 8 }).map((_, i) => (
              <div
                key={i}
                className="inline-block h-9 bg-paper-sunk rounded-chip animate-pulse"
                style={{ width: `${72 + ((i * 17) % 48)}px` }}
              />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="text-sm text-ink-3 p-6" data-testid="dept-empty">No departments yet — add your first one.</div>
        ) : (
          items.map((d) => (
            <div
              key={d.id}
              className="group inline-flex items-center gap-2 pl-3 pr-1 py-1 bg-paper-2 rounded-chip shadow-rest hover:shadow-raised transition-shadow"
              data-testid={`department-chip-${d.id}`}
            >
              <span className="font-display font-semibold text-sm tracking-wide text-ink whitespace-nowrap">
                {deptLabel(d.name)}
              </span>
              <button
                onClick={() => setPendingDelete(d)}
                aria-label={`Delete ${deptLabel(d.name)}`}
                className="h-7 w-7 flex items-center justify-center rounded-chip text-ink-3 hover:bg-status-urgent-bg hover:text-status-urgent transition-colors"
                data-testid={`delete-department-${d.id}`}
              >
                <X size={14} strokeWidth={2.5} />
              </button>
            </div>
          ))
        )}
      </div>
      )}

      <AddDeptModal open={showAdd} onCancel={() => setShowAdd(false)} onCreated={onCreated} />
      <ConfirmDeleteModal
        open={!!pendingDelete}
        dept={pendingDelete}
        onCancel={() => setPendingDelete(null)}
        onConfirm={doDelete}
        submitting={submitting}
      />
    </div>
  );
}
